/**
 * Author: Frontend Team
 * Generation: Animation System Event Chain Testing
 * Version: v3.0.124
 * Modified: 2025-09-14
 */

import React, { useEffect, useState } from 'react';
import webSocketService from '../services/websocket/WebSocketService';
import { AnimationEventMocker } from '../utils/AnimationEventMocker';

interface EventLog {
  timestamp: string;
  event: string;
  agentId: string;
  details: string;
  success: boolean;
}

const AnimationTestPage: React.FC = () => {
  const [logs, setLogs] = useState<EventLog[]>([]);
  const [mocker] = useState(() => new AnimationEventMocker());
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Enable mock mode
    webSocketService.enableMockMode();
    setIsConnected(true);

    // Setup event logging
    const originalConsoleLog = console.log;
    console.log = (...args) => {
      const message = args.join(' ');
      if (message.includes('SinglePathAnimationEngine') ||
          message.includes('PhaserCombatSequencer') ||
          message.includes('Animation')) {

        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, {
          timestamp,
          event: 'LOG',
          agentId: 'system',
          details: message,
          success: true
        }]);
      }
      originalConsoleLog(...args);
    };

    return () => {
      console.log = originalConsoleLog;
      webSocketService.disableMockMode();
    };
  }, []);

  const handleEventTest = (eventType: string) => {
    try {
      const result = mocker.triggerEvent(eventType);
      const timestamp = new Date().toLocaleTimeString();

      setLogs(prev => [...prev, {
        timestamp,
        event: eventType,
        agentId: result.agentId,
        details: JSON.stringify(result.data, null, 2),
        success: true
      }]);
    } catch (error) {
      const timestamp = new Date().toLocaleTimeString();
      setLogs(prev => [...prev, {
        timestamp,
        event: eventType,
        agentId: 'error',
        details: `Error: ${error}`,
        success: false
      }]);
    }
  };

  const clearLogs = () => setLogs([]);

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h1>🎮 Animation System Event Chain Test</h1>

      <div style={{ marginBottom: '20px' }}>
        <span style={{
          padding: '5px 10px',
          borderRadius: '5px',
          backgroundColor: isConnected ? '#90EE90' : '#FFB6C1',
          color: '#000'
        }}>
          WebSocket Mock Mode: {isConnected ? 'ENABLED' : 'DISABLED'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* Event Trigger Panel */}
        <div>
          <h2>🎯 Event Triggers</h2>

          <h3>📍 Lifecycle Events</h3>
          <button onClick={() => handleEventTest('AGENT_SPAWNED')} style={buttonStyle}>
            AGENT_SPAWNED
          </button>
          <button onClick={() => handleEventTest('AGENT_MOVED')} style={buttonStyle}>
            AGENT_MOVED
          </button>
          <button onClick={() => handleEventTest('AGENT_ELIMINATED')} style={buttonStyle}>
            AGENT_ELIMINATED
          </button>

          <h3>⚔️ Combat Events</h3>
          <button onClick={() => handleEventTest('COMBAT_STARTED')} style={buttonStyle}>
            COMBAT_STARTED
          </button>
          <button onClick={() => handleEventTest('COMBAT_DAMAGE_DEALT')} style={buttonStyle}>
            COMBAT_DAMAGE_DEALT
          </button>
          <button onClick={() => handleEventTest('COMBAT_ATTACK_MISSED')} style={buttonStyle}>
            COMBAT_ATTACK_MISSED
          </button>
          <button onClick={() => handleEventTest('COMBAT_ENDED')} style={buttonStyle}>
            COMBAT_ENDED
          </button>

          <h3>🎒 Resource Events</h3>
          <button onClick={() => handleEventTest('RESOURCE_COLLECTED')} style={buttonStyle}>
            RESOURCE_COLLECTED
          </button>
          <button onClick={() => handleEventTest('WEAPON_EQUIPPED')} style={buttonStyle}>
            WEAPON_EQUIPPED
          </button>
          <button onClick={() => handleEventTest('ITEM_USED')} style={buttonStyle}>
            ITEM_USED
          </button>

          <h3>🌍 Environment Events</h3>
          <button onClick={() => handleEventTest('SAFEZONE_DAMAGE_APPLIED')} style={buttonStyle}>
            SAFEZONE_DAMAGE_APPLIED
          </button>

          <div style={{ marginTop: '20px' }}>
            <button onClick={clearLogs} style={{...buttonStyle, backgroundColor: '#FF6B6B'}}>
              Clear Logs
            </button>
          </div>
        </div>

        {/* Event Log Panel */}
        <div>
          <h2>📋 Event Chain Logs</h2>
          <div style={{
            height: '600px',
            overflowY: 'auto',
            border: '1px solid #ccc',
            padding: '10px',
            backgroundColor: '#1e1e1e',
            color: '#fff',
            fontSize: '12px'
          }}>
            {logs.map((log, index) => (
              <div key={index} style={{
                marginBottom: '5px',
                padding: '5px',
                borderLeft: `3px solid ${log.success ? '#90EE90' : '#FF6B6B'}`,
                backgroundColor: log.success ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)'
              }}>
                <div style={{ fontSize: '10px', color: '#888' }}>
                  [{log.timestamp}] {log.event} → {log.agentId}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {log.details}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
        <h3>🔍 Expected Event Flow:</h3>
        <ol>
          <li>WebSocket Mock Event → BattleScene Event Handler</li>
          <li>BattleScene → SinglePathAnimationEngine.handle()</li>
          <li>Engine Priority Check → Scene/Combat Adapter</li>
          <li>Adapter → PhaserAnimationManager/PhaserCombatSequencer</li>
          <li>Animation Execution → Return to Idle</li>
        </ol>
      </div>
    </div>
  );
};

const buttonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px',
  margin: '5px 0',
  border: 'none',
  borderRadius: '5px',
  backgroundColor: '#4CAF50',
  color: 'white',
  cursor: 'pointer',
  fontSize: '14px'
};

export default AnimationTestPage;